import { useState, type FormEvent } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/db8dh7bk13ra8mig8psg';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    // Honeypot: silently absorb bot submissions
    const guard = (data.get('company_alt') as string | null)?.trim() ?? '';
    if (guard) {
      form.reset();
      setStatus('success');
      return;
    }

    data.delete('company_alt');
    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.code || responseText;

      const isSuccess =
        response.ok && parsed?.code === 'OK' && !`${serverMsg}`.includes('spam');

      if (isSuccess) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          `${serverMsg}`.includes('spam')
            ? '提交内容被判定为异常，请修改后重试。'
            : `提交失败：${serverMsg || '请稍后重试'}`,
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <form
      data-readdy-form
      id="about-contact-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h3 className="flex items-center gap-2 text-base font-black text-foreground-950">
        <i className="ri-mail-send-line text-primary-500"></i>
        在线留言
      </h3>
      <p className="mt-1.5 text-xs text-foreground-500">
        有线索、建议或版权问题，欢迎留言，我们会尽快处理。
      </p>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="cf-name" className="mb-1 block text-sm font-medium text-foreground-700">
            你的称呼
          </label>
          <input
            id="cf-name"
            name="name"
            type="text"
            required
            placeholder="请输入称呼"
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="cf-email" className="mb-1 block text-sm font-medium text-foreground-700">
            联系邮箱
          </label>
          <input
            id="cf-email"
            name="email"
            type="email"
            required
            placeholder="name@example.com"
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="cf-topic" className="mb-1 block text-sm font-medium text-foreground-700">
          留言类型
        </label>
        <select
          id="cf-topic"
          name="topic"
          className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
        >
          <option value="爆料线索">爆料线索</option>
          <option value="内容纠错">内容纠错</option>
          <option value="版权投诉">版权投诉</option>
          <option value="商务合作">商务合作</option>
          <option value="其他建议">其他建议</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="cf-message" className="mb-1 block text-sm font-medium text-foreground-700">
          留言内容
        </label>
        <textarea
          id="cf-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请描述你的问题或线索，最多 500 字。"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 p-3 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        ></textarea>
      </div>

      {/* Anti-spam guard field (not for users) */}
      <input
        className="form-guard-field"
        type="text"
        name="company_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="mt-4 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:px-8"
      >
        <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>
        {status === 'submitting' ? '提交中…' : '提交留言'}
      </button>

      {status === 'success' && (
        <p className="mt-3 flex items-center gap-1.5 text-sm text-primary-600">
          <i className="ri-checkbox-circle-line"></i>
          留言提交成功，感谢你的反馈！
        </p>
      )}
      {status === 'error' && (
        <p className="mt-3 flex items-center gap-1.5 text-sm text-accent-600">
          <i className="ri-error-warning-line"></i>
          {formError}
        </p>
      )}
    </form>
  );
}